"use client";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Mail, ShieldCheck } from "lucide-react";
import SEOHead from "../SEOHead";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import api, { setBlogAdminToken, getBlogAdminToken } from "../../lib/api";

/**
 * Two-step OTP login for the blog admin area. Only the allowlisted emails
 * (enforced by the backend) can receive a code. noindex/nofollow.
 */
export default function BlogAdminLogin() {
  const navigate = useNavigate();
  const [step, setStep] = useState(getBlogAdminToken() ? "done" : "email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  // If already logged in, go straight to the dashboard.
  if (step === "done") {
    navigate("/blog-admin", { replace: true });
    return null;
  }

  const requestOtp = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setLoading(true);
    try {
      const res = await api.blogAdmin.requestOtp(email.trim());
      setUserId(res.userId);
      setStep("otp");
      setInfo("A verification code was sent to your email.");
    } catch (err) {
      setError(err.message || "Unable to send code.");
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api.blogAdmin.verifyOtp(userId, otp.trim());
      setBlogAdminToken(res.token);
      navigate("/blog-admin", { replace: true });
    } catch (err) {
      setError(err.message || "Invalid code.");
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    setError("");
    setInfo("");
    try {
      await api.blogAdmin.resendOtp(userId);
      setInfo("A new code was sent.");
    } catch (err) {
      setError(err.message || "Unable to resend code.");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <SEOHead title="Blog Admin Login" noindex nofollow disableCanonical />

      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-[#252F4F] flex items-center justify-center">
            <ShieldCheck className="h-6 w-6 text-[#98C454]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[#252F4F]">Blog Admin</h1>
            <p className="text-sm text-gray-500">Authorized access only</p>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-lg bg-red-50 text-red-700 text-sm px-4 py-3">
            {error}
          </div>
        )}
        {info && (
          <div className="mb-4 rounded-lg bg-green-50 text-green-700 text-sm px-4 py-3">
            {info}
          </div>
        )}

        {step === "email" ? (
          <form onSubmit={requestOtp} className="space-y-5">
            <div>
              <Label htmlFor="email">Email address</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@bookatherapy.com"
                  className="pl-9"
                />
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-[#252F4F] hover:bg-[#1e2640]">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send verification code"}
            </Button>
          </form>
        ) : (
          <form onSubmit={verifyOtp} className="space-y-5">
            <div>
              <Label htmlFor="otp">Verification code</Label>
              <Input
                id="otp"
                inputMode="numeric"
                maxLength={6}
                required
                autoFocus
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                placeholder="123456"
                className="mt-1.5 text-center text-2xl tracking-[0.4em]"
              />
              <p className="text-xs text-gray-500 mt-2">
                Sent to <span className="font-medium">{email}</span>
              </p>
            </div>
            <Button
              type="submit"
              disabled={loading || otp.length !== 6}
              className="w-full bg-[#252F4F] hover:bg-[#1e2640]"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & sign in"}
            </Button>
            <div className="flex items-center justify-between text-sm">
              <button type="button" onClick={() => setStep("email")} className="text-gray-500 hover:text-[#252F4F]">
                ← Change email
              </button>
              <button type="button" onClick={resendOtp} className="text-[#252F4F] font-medium hover:underline">
                Resend code
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
